import * as i2 from '@ngrx/store';
import { createAction, props, createReducer, on, createFeatureSelector, createSelector, select, StoreModule } from '@ngrx/store';
import * as i0 from '@angular/core';
import { InjectionToken, Directive, Inject, Injectable, NgModule, Optional } from '@angular/core';
import * as i1$1 from '@ngrx/effects';
import { createEffect, ofType, EffectsModule } from '@ngrx/effects';
import { SIDE_DRAWER_OUTLET, SideDrawerModule } from '@fleetmatics/ui.base-library/side-drawer';
import * as i1 from '@angular/router';
import { filter, switchMap, take, switchMapTo, tap, mapTo, map, withLatestFrom } from 'rxjs/operators';
import * as i2$1 from '@fleetmatics/ui.notifications/notifications-api';
import { NOTIFICATIONS_API_BASE_PATH, NotificationsService } from '@fleetmatics/ui.notifications/notifications-api';

/*!
 * Copyright © 2020. Verizon Connect Ireland Limited. All rights reserved.
 */

/*!
 * Copyright © 2020. Verizon Connect Ireland Limited. All rights reserved.
 */
const addNotifications = createAction('[Notifications] Add Notifications', props());
const addHiddenNotifications = createAction('[Notifications] Add Hidden Notifications', props());
const addNotificationsSuccess = createAction('[Notifications] Add Notifications Success', props());
const mergeNewNotifications = createAction('[Notifications] Merge New Notifications', props());
const clearNumberOfNewNotifications = createAction('[Notifications] Clear Number Of New Notifications');
const clearNumberOfNewNotificationsSuccess = createAction('[Notifications] Clear Number Of New Notifications Success');
const getNumberOfNewNotifications = createAction('[Notifications] Get Number Of New Notifications');
const getNumberOfNewNotificationsSuccess = createAction('[Notifications] Get Number Of New Notifications Success', props());
const getUserNotifications = createAction('[Notifications] Get User Notifications', props());
const getUserNotificationsSuccess = createAction('[Notifications] Get User Notifications Success', props());
const saveUserNotificationsRequestTime = createAction('[Notifications] Set Time for User Notifications Request');
const connectorHostInit = createAction('[Notifications] Connector Host Initialization');
const markNotificationsAsRead = createAction('[Notifications] Mark Notifications as Read', props());
const markNotificationsAsReadSuccess = createAction('[Notifications] Mark Notifications as Read Success', props());
const clearPendingReadNotifications = createAction('[Notifications] Clear Pending Read Notifications', props());
const dismissNotifications = createAction('[Notifications] Dismiss Notifications', props());
const dismissNotificationsSuccess = createAction('[Notifications] Dismiss Notifications Success', props());
const showHiddenNotifications = createAction('[Notifications] Show Hidden Notifications');

/*!
 * Copyright © 2020-2024. Verizon Connect Ireland Limited. All rights reserved.
 */
const initialState = {
    numberOfNewNotifications: 0,
    dateTimeOfLatestRequest: '',
    notifications: {},
    hiddenNotifications: [],
    notificationsPipelineSettings: {
        isNotificationsPipelineFeatureEnabled: true,
        notificationsBellBadgeStyle: 'numeric',
    },
};

/*!
 * Copyright © 2020-2024. Verizon Connect Ireland Limited. All rights reserved.
 */
const notificationsFeatureKey = 'notifications';
const notificationsReducer = createReducer(initialState, on(addNotificationsSuccess, (state, action) => {
    const notificationsToInsert = filterNotifications(state.notifications, action.notifications);
    const orderedNotificationDict = addToNotificationsDict(state.notifications, notificationsToInsert);
    return {
        numberOfNewNotifications: state.numberOfNewNotifications + notificationsToInsert.length,
        notifications: orderedNotificationDict,
        hiddenNotifications: [...state.hiddenNotifications],
        notificationsPipelineSettings: { ...state.notificationsPipelineSettings },
    };
}), on(mergeNewNotifications, (state, action) => {
    const orderedNotificationDict = mergeNewNotificationsDict(state.notifications, action.notifications);
    return {
        ...state,
        notifications: orderedNotificationDict,
    };
}), on(addHiddenNotifications, (state, action) => {
    let notificationsToInsert;
    notificationsToInsert = action.notifications.filter((notification, i, arr) => arr.findIndex(n => n.id === notification.id) === i);
    notificationsToInsert = notificationsToInsert.filter(notification => {
        const notificationFound = state.hiddenNotifications?.filter(ntfn => ntfn.id === notification.id);
        return notificationFound == null || notificationFound.length === 0;
    });
    notificationsToInsert = filterNotifications(state.notifications, notificationsToInsert);
    return {
        ...state,
        hiddenNotifications: [...state.hiddenNotifications, ...notificationsToInsert],
        numberOfNewNotifications: state.numberOfNewNotifications + notificationsToInsert.length,
    };
}), on(markNotificationsAsReadSuccess, (state, action) => {
    const idsToUpdate = action.notificationIds;
    return updateNotificationsReadState(state, 'unread', 'pending-read', { idsToUpdate });
}), on(clearPendingReadNotifications, (state, action) => {
    const idsToUpdate = action.notificationIds;
    return updateNotificationsReadState(state, 'pending-read', 'read', { idsToUpdate });
}), on(showHiddenNotifications, (state, action) => {
    const orderedNotificationsDict = addToNotificationsDict(state.notifications, state.hiddenNotifications, 'unread');
    return {
        ...state,
        hiddenNotifications: [],
        notifications: orderedNotificationsDict,
    };
}), on(getUserNotificationsSuccess, (state, action) => ({
    ...state,
    notifications: action.notificationsDict,
})), on(saveUserNotificationsRequestTime, (state, action) => ({
    ...state,
    dateTimeOfLatestRequest: new Date().toISOString(),
})), on(clearNumberOfNewNotificationsSuccess, (state, action) => ({
    ...state,
    numberOfNewNotifications: 0,
})), on(getNumberOfNewNotificationsSuccess, (state, action) => ({
    ...state,
    numberOfNewNotifications: action.numberOfNewNotifications,
})), on(dismissNotificationsSuccess, (state, action) => {
    const stateCopy = { ...state };
    stateCopy.notifications = { ...state.notifications };
    const currentStateNotifications = state.notifications;
    if (action.groupKey) {
        delete stateCopy.notifications[action.groupKey];
    }
    else {
        for (const group in currentStateNotifications) {
            if (currentStateNotifications.hasOwnProperty(group)) {
                stateCopy.notifications[group] = currentStateNotifications[group].filter(x => action.notificationIds[0] !== x.id);
                if (stateCopy.notifications[group].length === 0) {
                    delete stateCopy.notifications[group];
                }
            }
        }
    }
    return stateCopy;
}));
const updateNotificationsReadState = (state, prevReadState, readState, optional) => {
    const groupKey = optional?.groupKey;
    const idsToUpdate = optional?.idsToUpdate;
    const stateCopy = { ...state };
    stateCopy.notifications = { ...state.notifications };
    const currentStateNotifications = state.notifications;
    for (const group in currentStateNotifications) {
        if (currentStateNotifications.hasOwnProperty(group)) {
            const groupToUpdate = currentStateNotifications[group];
            const shouldUpdateGroup = (!groupKey && !idsToUpdate) ||
                (groupKey && groupKey === group) ||
                (idsToUpdate && groupToUpdate.find(not => idsToUpdate.includes(not.id)));
            stateCopy.notifications[group] = shouldUpdateGroup
                ? groupToUpdate.map(n => ({ ...n, readState: n.readState === prevReadState ? readState : n.readState }))
                : [...groupToUpdate];
        }
    }
    return stateCopy;
};
const filterNotifications = (notifications, newNotifications) => {
    const notificationsStateCopy = { ...notifications };
    newNotifications = newNotifications.filter((notification, i, arr) => arr.findIndex(n => n.id === notification.id) === i);
    const notificationsToInsert = newNotifications.filter(notification => {
        const notificationsByGroup = notificationsStateCopy[notification.groupKey];
        const notificationFound = notificationsByGroup?.filter(ntfn => ntfn.id === notification.id);
        return notificationFound == null || notificationFound.length === 0;
    });
    return notificationsToInsert;
};
const addToNotificationsDict = (notifications, newNotifications, readState) => {
    let orderedState = {};
    const oldStateCopy = { ...notifications };
    const notificationsToInsert = newNotifications.slice().reverse();
    // @todo add sort comparer so that they appear in order .sort((not1,not2)=>not1.)
    notificationsToInsert.forEach((insertedNotification) => {
        const notification = { ...insertedNotification, readState: readState ?? insertedNotification.readState };
        if (orderedState[notification.groupKey]) {
            orderedState[notification.groupKey] = [notification, ...orderedState[notification.groupKey]];
        }
        else {
            orderedState[notification.groupKey] = [notification, ...(oldStateCopy[notification.groupKey] ?? [])];
            delete oldStateCopy[notification.groupKey];
        }
    });
    orderedState = { ...orderedState, ...oldStateCopy };
    return orderedState;
};
const mergeNewNotificationsDict = (notifications, newNotifications) => {
    let orderedState = {};
    const oldStateCopy = { ...notifications };
    for (const groupKey in newNotifications) {
        if (newNotifications.hasOwnProperty(groupKey)) {
            if (oldStateCopy[groupKey]) {
                orderedState[groupKey] = [...newNotifications[groupKey], ...oldStateCopy[groupKey]];
                delete oldStateCopy[groupKey];
            }
            else {
                orderedState[groupKey] = newNotifications[groupKey];
            }
        }
    }
    orderedState = { ...orderedState, ...oldStateCopy };
    return orderedState;
};
function reducer(state, action) {
    return notificationsReducer(state, action);
}

/*!
 * Copyright © 2020-2024. Verizon Connect Ireland Limited. All rights reserved.
 */
const selectNotificationsState = createFeatureSelector(notificationsFeatureKey);
const selectNotifications = (validateNotificationType) => createSelector(selectNotificationsState, (state) => {
    if (validateNotificationType) {
        const filteredNotifications = {};
        Object.entries(state.notifications).forEach(([groupKey, groupKeyNotifications]) => {
            const filteredGroupKeyNotifications = groupKeyNotifications.reduce((notifications, notification) => {
                if (notification.data?.metadata?.alertType && validateNotificationType(notification.data.metadata.alertType)) {
                    notifications.push({ ...notification, type: notification.data.metadata.alertType });
                }
                else if (validateNotificationType(notification.type)) {
                    notifications.push({ ...notification });
                }
                return notifications;
            }, []);
            if (filteredGroupKeyNotifications.length) {
                filteredNotifications[groupKey] = filteredGroupKeyNotifications;
            }
        });
        return filteredNotifications;
    }
    return state.notifications;
});
const selectNumberOfNewNotifications = createSelector(selectNotificationsState, (state) => state.numberOfNewNotifications);
const selectNotificationsPipelineSettings = createSelector(selectNotificationsState, (state) => state.notificationsPipelineSettings);
const selectNotificationsByGroup = (groupKey, validateNotificationType) => createSelector(selectNotifications(validateNotificationType), (state) => state[groupKey]);
const selectHiddenNotifications = (groupKey) => createSelector(selectNotificationsState, (state) => groupKey ? state.hiddenNotifications.filter(n => n.groupKey === groupKey) : state.hiddenNotifications);
const selectNumberOfHiddenNotifications = (groupKey) => createSelector(selectHiddenNotifications(groupKey), (notifications) => notifications?.length);
const selectDateTimeOfLastRequest = createSelector(selectNotificationsState, (state) => state.dateTimeOfLatestRequest);
const selectNotificationStacks = (validateNotificationType) => createSelector(selectNotifications(validateNotificationType), (notificationsDict) => {
    const stacks = [];
    for (const key in notificationsDict) {
        if (notificationsDict.hasOwnProperty(key)) {
            const notificationsOfType = notificationsDict[key];
            const topOfStack = {
                ...notificationsOfType[0],
                stackLength: notificationsOfType.length,
            };
            if (notificationsOfType.length > 0) {
                stacks.push(topOfStack);
            }
        }
    }
    return stacks;
});
const selectIsNotificationPipelineFeatureEnabled = createSelector(selectNotificationsPipelineSettings, (state) => state.isNotificationsPipelineFeatureEnabled);
const selectNotifiationsBadgeStyle = createSelector(selectNotificationsPipelineSettings, (state) => state.notificationsBellBadgeStyle);
const selectNavbarNotificationsBellProperties = createSelector(selectNotificationsState, (state) => {
    return {
        isNotificationsBellEnabled: state.notificationsPipelineSettings.isNotificationsPipelineFeatureEnabled,
        numberOfNewNotifications: state.numberOfNewNotifications,
        notificationBellbadgeStyle: state.notificationsPipelineSettings.notificationsBellBadgeStyle,
    };
});

/*!
 * Copyright © 2020. Verizon Connect Ireland Limited. All rights reserved.
 */

/*!
 * Copyright © 2020. Verizon Connect Ireland Limited. All rights reserved.
 */

/*!
 * Copyright © 2020. Verizon Connect Ireland Limited. All rights reserved.
 */

/*
 * Copyright © 2024. Verizon Connect Ireland Limited. All rights reserved.
 */
const NOTIFICATION_CONNECTOR_CONFIGS = new InjectionToken('@fleetmatics/ui.notifications connector configs');

/*!
 * Copyright © 2020-2024. Verizon Connect Ireland Limited. All rights reserved.
 */
const NOTIFICATIONS_PATH = 'notifications';
class NotificationsConnectorDirective {
    constructor(router, store, configSvc) {
        this.router = router;
        this.store = store;
        this.configSvc = configSvc;
        this.numberOfNewNotifications$ = this.store.pipe(select(selectNumberOfNewNotifications));
        this.isNotificationsPipelineFeatureEnabled$ = this.store.pipe(select(selectIsNotificationPipelineFeatureEnabled));
        this.notificationsBellBadgeStyle$ = this.store.pipe(select(selectNotifiationsBadgeStyle));
    }
    ngOnInit() {
        if (this.configSvc.notificationPipeLineSettings.isPipelineEnabledForMicrosite) {
            this.store.dispatch(connectorHostInit());
        }
    }
    toggle() {
        const routeConfig = this.router.routerState?.root?.children.find(childRoute => childRoute.outlet === SIDE_DRAWER_OUTLET && childRoute.snapshot.url.find(u => u.path === NOTIFICATIONS_PATH));
        if (routeConfig) {
            this.router.navigate([{ outlets: { [SIDE_DRAWER_OUTLET]: null } }], { queryParamsHandling: 'preserve' });
        }
        else {
            this.store.dispatch(showHiddenNotifications());
            this.router.navigate([{ outlets: { [SIDE_DRAWER_OUTLET]: [NOTIFICATIONS_PATH] } }], { queryParamsHandling: 'preserve' });
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NotificationsConnectorDirective, deps: [{ token: i1.Router }, { token: i2.Store }, { token: NOTIFICATION_CONNECTOR_CONFIGS }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.2.12", type: NotificationsConnectorDirective, selector: "[ntfnConnector]", exportAs: ["ntfnConnector"], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NotificationsConnectorDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[ntfnConnector]',
                    exportAs: 'ntfnConnector',
                }]
        }], ctorParameters: function () { return [{ type: i1.Router }, { type: i2.Store }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [NOTIFICATION_CONNECTOR_CONFIGS]
                }] }]; } });

/*!
 * Copyright © 2022-2024. Verizon Connect Ireland Limited. All rights reserved.
 */
const GROUP_KEY_TRANSLATIONS = {
    S_NOTIFICATIONS_GET_STARTED_GROUP: $localize `:@@S_NOTIFICATIONS_GET_STARTED_GROUP:Get Started`,
    S_NOTIFICATIONS_BUILTIN_TELEMATICS_GROUP: $localize `:@@S_NOTIFICATIONS_BUILTIN_TELEMATICS_GROUP:Built-in telematics`,
};
class TranslationService {
    localizeGroupKey(groupKeyTranslationKey) {
        return (!!groupKeyTranslationKey && GROUP_KEY_TRANSLATIONS[groupKeyTranslationKey]) || undefined;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: TranslationService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: TranslationService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: TranslationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

/*!
 * Copyright © 2020-2024. Verizon Connect Ireland Limited. All rights reserved.
 */
class NotificationsEffects {
    constructor(actions$, notificationsApiSvc, store, configSvc, router, translationService) {
        this.actions$ = actions$;
        this.notificationsApiSvc = notificationsApiSvc;
        this.store = store;
        this.configSvc = configSvc;
        this.router = router;
        this.translationService = translationService;
        this.onConnectorInit$ = createEffect(() => this.actions$.pipe(ofType(connectorHostInit), filter(() => this.configSvc.notificationPipeLineSettings.isPipelineEnabledForMicrosite), switchMap(() => this.store.pipe(select(selectIsNotificationPipelineFeatureEnabled)).pipe(filter(isFeatureEnabled => isFeatureEnabled), take(1), switchMapTo([getNumberOfNewNotifications(), getUserNotifications({})])))));
        this.onClearNewNotificationsFireAndForgetEffect$ = createEffect(() => this.actions$.pipe(ofType(clearNumberOfNewNotifications), tap(() => {
            const utcTime = new Date().toISOString();
            this.notificationsApiSvc.v1NotificationsMarkNotificationsAsNotNewPut(utcTime).subscribe();
        }), mapTo(clearNumberOfNewNotificationsSuccess())));
        this.onGetNumberOfNewNotifications$ = createEffect(() => this.actions$.pipe(ofType(getNumberOfNewNotifications), switchMap(() => this.notificationsApiSvc.v1NotificationsGetNumberOfNewNotificationsGet()), map((numberOfNewNotifications) => this.isPanelOpen() ? clearNumberOfNewNotifications() : getNumberOfNewNotificationsSuccess({ numberOfNewNotifications }))));
        this.onGetUserNotifications$ = createEffect(() => this.actions$.pipe(ofType(getUserNotifications), filter(action => !action.startDate), tap(() => this.store.dispatch(saveUserNotificationsRequestTime())), switchMap(action => this.notificationsApiSvc.v1NotificationsGet()), map(notificationsDTO => this.mapNotificationsResponseToDict(notificationsDTO)), map(notificationsDict => getUserNotificationsSuccess({ notificationsDict }))));
        this.onGetNewUserNotifications$ = createEffect(() => this.actions$.pipe(ofType(getUserNotifications), filter(action => !!action.startDate), tap(() => this.store.dispatch(saveUserNotificationsRequestTime())), switchMap(action => this.notificationsApiSvc.v1NotificationsGet(action.startDate)), map(notificationsDTO => this.mapNotificationsResponseToDict(notificationsDTO)), map(notificationsDict => mergeNewNotifications({
            notifications: notificationsDict,
        }))));
        this.onMarkNotificationsAsRead$ = createEffect(() => this.actions$.pipe(ofType(markNotificationsAsRead), switchMap((payload) => this.notificationsApiSvc
            .v1NotificationsMarkNotificationsAsReadPut(true, payload.notificationIds)
            .pipe(mapTo(markNotificationsAsReadSuccess({ notificationIds: payload.notificationIds }))))));
        this.onDismissNotifications$ = createEffect(() => this.actions$.pipe(ofType(dismissNotifications), switchMap((payload) => this.notificationsApiSvc
            .v1NotificationsDeleteRenderedNotificationsPost(payload.notificationIds)
            .pipe(mapTo(dismissNotificationsSuccess({ notificationIds: payload.notificationIds, groupKey: payload.groupKey }))))));
        this.onAddNewNotifications$ = createEffect(() => this.actions$.pipe(ofType(addNotifications), map(({ notifications }) => notifications.map(cn => {
            if (!!cn.Data) {
                return this.mapRevealGenericNotificationsDataToNotificationDTO(cn.Data);
            }
            return this.mapGenericNotificationsDataToLocalizedGroupKeyNotificationDTO(cn);
        })), map(notifications => (this.isPanelOpen() ? addHiddenNotifications({ notifications }) : addNotificationsSuccess({ notifications })))));
        this.onShowHiddenNotifications$ = createEffect(() => this.actions$.pipe(ofType(showHiddenNotifications), withLatestFrom(this.store.pipe(select(selectNumberOfNewNotifications)), this.store.pipe(select(selectNumberOfHiddenNotifications()))), tap(([, numberOfNewNotifications, numberOfHiddenNotifications]) => {
            if (numberOfNewNotifications > 0 || (typeof numberOfHiddenNotifications === 'number' && numberOfHiddenNotifications > 0)) {
                this.store.dispatch(clearNumberOfNewNotifications());
            }
        })), { dispatch: false });
        this.mapRenderedMessageDTOToNotification = (renderedNotification) => ({
            id: renderedNotification.notificationId || '',
            groupKey: (renderedNotification.metadata && this.translationService.localizeGroupKey(renderedNotification.metadata?.groupKeyTranslationKey)) ||
                renderedNotification.groupKey ||
                '',
            type: renderedNotification.notificationType || '',
            readState: renderedNotification.read ? 'read' : 'unread',
            data: this.mapRenderedNotificationToNotificationData(renderedNotification),
        });
        this.mapRenderedNotificationToNotificationData = (renderedNotification) => ({
            ...renderedNotification,
            renderedData: { ...renderedNotification.renderedData },
            metaData: { ...renderedNotification.metadata },
        });
        this.mapRevealGenericNotificationsDataToNotificationDTO = (notificationData) => {
            const notification = {
                id: notificationData.notificationId,
                groupKey: notificationData.groupKey,
                type: notificationData.notificationType,
                readState: 'unread',
                data: this.mapGenericNotificationsDataToNotificationData(notificationData),
            };
            return this.mapGenericNotificationsDataToLocalizedGroupKeyNotificationDTO(notification);
        };
        this.mapGenericNotificationsDataToLocalizedGroupKeyNotificationDTO = (notificationData) => {
            if (notificationData.groupKey) {
                const metadata = notificationData.metadata || (!!notificationData.data && notificationData.data.metadata);
                return {
                    ...notificationData,
                    groupKey: this.translationService.localizeGroupKey(metadata?.groupKeyTranslationKey) || notificationData.groupKey,
                };
            }
            return notificationData;
        };
    }
    mapNotificationsResponseToDict(notificationsDTO) {
        const result = {};
        const dtoStacks = notificationsDTO.notificationsStack;
        for (const typeKey in dtoStacks) {
            if (dtoStacks.hasOwnProperty(typeKey)) {
                dtoStacks[typeKey].forEach((notificationDTO) => {
                    const notification = this.mapRenderedMessageDTOToNotification(notificationDTO);
                    (result[notification.groupKey] = result[notification.groupKey] || []).push(notification);
                });
            }
        }
        return result;
    }
    mapGenericNotificationsDataToNotificationData(notificationData) {
        const createObjectFromPrefixedProps = (obj, prefix) => Object.fromEntries(Object.entries(obj)
            .filter(([key]) => key.startsWith(prefix))
            .map(([key, data]) => [key.replace(prefix, ''), data]));
        const metaDataPrefix = 'metadata_';
        const metadata = createObjectFromPrefixedProps(notificationData, metaDataPrefix);
        const renderedDataPrefix = 'renderedData_';
        const renderedData = createObjectFromPrefixedProps(notificationData, renderedDataPrefix);
        return {
            ...notificationData,
            metadata,
            renderedData,
        };
    }
    isPanelOpen() {
        return !!this.router.routerState?.root?.children.find(childRoute => childRoute.outlet === SIDE_DRAWER_OUTLET);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NotificationsEffects, deps: [{ token: i1$1.Actions }, { token: i2$1.NotificationsService }, { token: i2.Store }, { token: NOTIFICATION_CONNECTOR_CONFIGS }, { token: i1.Router }, { token: TranslationService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NotificationsEffects }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NotificationsEffects, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1$1.Actions }, { type: i2$1.NotificationsService }, { type: i2.Store }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [NOTIFICATION_CONNECTOR_CONFIGS]
                }] }, { type: i1.Router }, { type: TranslationService }]; } });

/*
 * Copyright © 2020. Verizon Connect Ireland Limited. All rights reserved.
 */
class SideDrawerGuard {
    constructor(store) {
        this.store = store;
    }
    canActivate(next, state) {
        return this.store.pipe(select(selectIsNotificationPipelineFeatureEnabled));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SideDrawerGuard, deps: [{ token: i2.Store }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SideDrawerGuard, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SideDrawerGuard, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i2.Store }]; } });

/*!
 * Copyright © 2020-2024. Verizon Connect Ireland Limited. All rights reserved.
 */
const LOAD_CHILDREN = new InjectionToken('loadChildren');
class NotificationsConnectorModule {
    static forRoot(configs, notificationRoutFn) {
        return {
            ngModule: NotificationsConnectorModule,
            providers: [{
                    provide: NOTIFICATIONS_API_BASE_PATH,
                    useValue: configs.notificationPipeLineSettings.notificationsApiPath,
                },
                { provide: NOTIFICATION_CONNECTOR_CONFIGS, useValue: configs },
                { provide: LOAD_CHILDREN, useValue: notificationRoutFn },
            ],
        };
    }
    constructor(notificationRoutFn, router) {
        if (!notificationRoutFn) {
            return;
        }
        router.config.unshift({
            path: NOTIFICATIONS_PATH,
            loadChildren: notificationRoutFn,
            outlet: SIDE_DRAWER_OUTLET,
        });
        router.resetConfig(router.config);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NotificationsConnectorModule, deps: [{ token: LOAD_CHILDREN, optional: true }, { token: i1.Router }], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: NotificationsConnectorModule, declarations: [NotificationsConnectorDirective], imports: [SideDrawerModule, i2.StoreFeatureModule, i1$1.EffectsFeatureModule], exports: [NotificationsConnectorDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NotificationsConnectorModule, providers: [
            NotificationsService,
            SideDrawerGuard,
        ], imports: [SideDrawerModule, StoreModule.forFeature(notificationsFeatureKey, reducer), EffectsModule.forFeature([NotificationsEffects])] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NotificationsConnectorModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [NotificationsConnectorDirective],
                    imports: [SideDrawerModule, StoreModule.forFeature(notificationsFeatureKey, reducer), EffectsModule.forFeature([NotificationsEffects])],
                    exports: [NotificationsConnectorDirective],
                    providers: [
                        NotificationsService,
                        SideDrawerGuard,
                    ],
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [LOAD_CHILDREN]
                }] }, { type: i1.Router }]; } });

/*!
 * Copyright © 2020. Verizon Connect Ireland Limited. All rights reserved.
 */
/*
 * Public API Surface of notifications-connector
 */

/**
 * Generated bundle index. Do not edit.
 */

export { LOAD_CHILDREN, NOTIFICATIONS_PATH, NOTIFICATION_CONNECTOR_CONFIGS, NotificationsConnectorDirective, NotificationsConnectorModule, SideDrawerGuard, addHiddenNotifications, addNotifications, addNotificationsSuccess, clearNumberOfNewNotifications, clearNumberOfNewNotificationsSuccess, clearPendingReadNotifications, connectorHostInit, dismissNotifications, dismissNotificationsSuccess, getNumberOfNewNotifications, getNumberOfNewNotificationsSuccess, getUserNotifications, getUserNotificationsSuccess, initialState, markNotificationsAsRead, markNotificationsAsReadSuccess, mergeNewNotifications, saveUserNotificationsRequestTime, selectDateTimeOfLastRequest, selectHiddenNotifications, selectIsNotificationPipelineFeatureEnabled, selectNavbarNotificationsBellProperties, selectNotifiationsBadgeStyle, selectNotificationStacks, selectNotifications, selectNotificationsByGroup, selectNotificationsPipelineSettings, selectNotificationsState, selectNumberOfHiddenNotifications, selectNumberOfNewNotifications, showHiddenNotifications };
